import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import debounce from 'lodash/debounce';
import { searchEntityRecords, AlgoliaRecordHit } from '../services/searchEntityRecords';
import { EntityRecord } from '#types/System.types';
import { DEFAULT_SEARCH_DEBOUNCE_MS, DEFAULT_SEARCH_MIN_CHARS, DEFAULT_SEARCH_HITS_PER_PAGE } from '#types/System.Parameters.types';

/**
 * Parameters for the useEntityRecordSearch hook
 */
interface UseEntityRecordSearchParams {
  /** Entity ID to search records for */
  entityId: string;
  /** Optional entity group ID to filter results */
  entityGroupId?: string;
  /** Cache records to merge with Algolia results */
  cacheRecords: EntityRecord[];
  /** Maximum number of Algolia results to return */
  hitsPerPage?: number;
  /** Debounce delay in milliseconds */
  debounceMs?: number;
}

/**
 * Return value from useEntityRecordSearch hook
 */
interface UseEntityRecordSearchReturn {
  /** Combined search results (Algolia + cache) */
  searchResults: EntityRecord[];
  /** Whether a search is currently in progress */
  isSearching: boolean;
  /** Error message if search failed */
  searchError: string | null;
  /** Execute a search query */
  search: (query: string) => void;
  /** Clear search results */
  clearSearch: () => void;
}

/**
 * Incremental filtering state for cache-only matching.
 * Tracks the previous query and its matching records to enable O(k) filtering
 * when the user types additional characters (query extension).
 * Also tracks cacheIdentity to detect when cache has changed and reset is needed.
 */
interface IncrementalFilterState {
  query: string;
  matchingRecords: EntityRecord[];
  cacheIdentity: EntityRecord[] | null;
}

/**
 * Hook for searching entity records using Algolia with cache fallback
 * 
 * Strategy:
 * 1. Always compute cache matches locally (fast, cache-first).
 * 2. When query length >= DEFAULT_SEARCH_MIN_CHARS, query Algolia (debounced) with filters for entityId and optional entityGroupId.
 * 3. Merge by docId while preserving Algolia ordering:
 *    - If a docId exists in cache, prefer cache (registry is authoritative for displayed values).
 *    - Otherwise use Algolia.
 * 4. Append cache-only matches (not in Algolia hit set), capped for UI performance.
 * 5. Use incremental filtering for cache-only matching: when query is an extension of the previous query,
 *    filter only the previous matches instead of the full cache (O(k) instead of O(n)).
 * 
 * This ensures:
 * - Fast full-text search via Algolia
 * - Cache is authoritative for displayed values when available
 * - Recently created/updated records visible via cache fallback
 * - Request lifecycle correctness under races (latest-only updates)
 * - Efficient typeahead via incremental filtering
 * 
 * @param params - Search configuration
 * @returns Search results and controls
 */
export const useEntityRecordSearch = ({
  entityId,
  entityGroupId,
  cacheRecords,
  hitsPerPage = DEFAULT_SEARCH_HITS_PER_PAGE,
  debounceMs = DEFAULT_SEARCH_DEBOUNCE_MS,
}: UseEntityRecordSearchParams): UseEntityRecordSearchReturn => {
  const [algoliaResults, setAlgoliaResults] = useState<AlgoliaRecordHit[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [currentQuery, setCurrentQuery] = useState('');

  const latestRequestSeqRef = useRef(0);
  
  const incrementalFilterRef = useRef<IncrementalFilterState>({
    query: '',
    matchingRecords: [],
    cacheIdentity: null,
  });

  const invalidateInFlightRequests = useCallback(() => {
    latestRequestSeqRef.current += 1;
  }, []);

  /**
   * Debounced search function
   */
  const debouncedSearch = useMemo(() =>
    debounce(async (query: string, seq: number) => {
      if (!query.trim()) {
        if (latestRequestSeqRef.current === seq) {
          setAlgoliaResults([]);
          setIsSearching(false);
        }
        return;
      }

      try {
        const hits = await searchEntityRecords({ entityId, entityGroupId, query, hitsPerPage });
        if (latestRequestSeqRef.current === seq) {
          setAlgoliaResults(hits);
          setSearchError(null);
        }
      } catch (error) {
        if (latestRequestSeqRef.current === seq) {
          if (error instanceof Error) {
            console.warn('[useEntityRecordSearch] Algolia search failed:', error);
            setSearchError(error.message);
          } else {
            setSearchError('Search failed');
          }
        }
      } finally {
        if (latestRequestSeqRef.current === seq) {
          setIsSearching(false);
        }
      }
    }, debounceMs),
  [entityId, entityGroupId, hitsPerPage, debounceMs]);

  /**
   * Public search function
   */
  const search = useCallback((query: string) => {
    setCurrentQuery(query);
    
    if (!query.trim()) {
      invalidateInFlightRequests();
      setAlgoliaResults([]);
      setIsSearching(false);
      debouncedSearch.cancel();
      return;
    }

    // Only trigger Algolia search if query meets minimum character requirement
    // For shorter queries, show cache-filtered results only
    if (query.trim().length < DEFAULT_SEARCH_MIN_CHARS) {
      invalidateInFlightRequests();
      setAlgoliaResults([]);
      setIsSearching(false);
      debouncedSearch.cancel();
      return;
    }

    const seq = latestRequestSeqRef.current + 1;
    latestRequestSeqRef.current = seq;
    setIsSearching(true);
    debouncedSearch(query, seq);
  }, [debouncedSearch, invalidateInFlightRequests]);

  /**
   * Clear search results and reset incremental filtering state
   */
  const clearSearch = useCallback(() => {
    setCurrentQuery('');
    invalidateInFlightRequests();
    setAlgoliaResults([]);
    setIsSearching(false);
    setSearchError(null);
    debouncedSearch.cancel();
    incrementalFilterRef.current = { query: '', matchingRecords: [], cacheIdentity: null };
  }, [debouncedSearch, invalidateInFlightRequests]);

  useEffect(() => {
    return () => {
      invalidateInFlightRequests();
      debouncedSearch.cancel();
    };
  }, [debouncedSearch, invalidateInFlightRequests]);

  /**
   * Filter cache records with incremental optimization.
   * 
   * When the new query is an extension of the previous query (e.g., "ac" → "acm"),
   * filter only the previous matching records instead of the full cache.
   * This reduces complexity from O(n) to O(k) where k is the previous result set size.
   * 
   * Cache identity check: If the cacheRecords array reference has changed since
   * the last filter, we reset and filter the full cache (stale data protection).
   * 
   * @param queryLower - Lowercase trimmed query string
   * @param fullCacheRecords - All cache records to potentially filter
   * @param algoliaDocIds - Set of docIds already returned by Algolia (to exclude)
   * @param cacheRecordsRef - The current cacheRecords array reference for identity check
   * @returns Filtered cache-only records
   */
  const filterCacheRecordsIncrementally = useCallback((
    queryLower: string,
    fullCacheRecords: EntityRecord[],
    algoliaDocIds: Set<string>,
    cacheRecordsRef: EntityRecord[]
  ): EntityRecord[] => {
    if (!queryLower) {
      incrementalFilterRef.current = { query: '', matchingRecords: [], cacheIdentity: null };
      return [];
    }

    const { query: lastQuery, matchingRecords: lastMatches, cacheIdentity } = incrementalFilterRef.current;

    const cacheChanged = cacheIdentity !== cacheRecordsRef;
    const isIncremental = !cacheChanged && lastQuery && queryLower.startsWith(lastQuery);
    const searchSpace = isIncremental ? lastMatches : fullCacheRecords;

    const matches = searchSpace.filter((record) => {
      if (record.docId && algoliaDocIds.has(record.docId)) {
        return false;
      }

      const nameMatch = record.meta?.searchNameLowercase?.includes(queryLower) ||
        record.name?.toLowerCase().includes(queryLower);

      return Boolean(nameMatch);
    });

    incrementalFilterRef.current = { query: queryLower, matchingRecords: matches, cacheIdentity: cacheRecordsRef };

    return matches.slice(0, 200);
  }, []);

  /**
   * Merge Algolia results with cache (docId-based).
   *
   * Semantics:
   * - Preserve Algolia ordering for discovery/ranking.
   * - If the same docId exists in cache, prefer cache (registry is authoritative for display values).
   * - Append cache-only matches (not in Algolia hit set) with a cap.
   * - Use incremental filtering for cache-only matching to optimize typeahead performance.
   */
  const searchResults = useMemo((): EntityRecord[] => {
    if (!currentQuery.trim()) {
      incrementalFilterRef.current = { query: '', matchingRecords: [], cacheIdentity: null };
      return [];
    }

    const cacheByDocId = new Map<string, EntityRecord>();
    cacheRecords.forEach((record) => {
      if (!record.docId) return;
      cacheByDocId.set(record.docId, record);
    });

    const algoliaDocIds = new Set<string>(algoliaResults.map(hit => hit.objectID).filter(Boolean));

    const algoliaAsRecords: EntityRecord[] = algoliaResults.map(hit => ({
      docId: hit.objectID,
      name: hit.name || '',
      status: hit.status as EntityRecord['status'],
      entityGroupIds: hit.entityGroupIds,
      fields: hit.fields,
      meta: hit.meta,
    }));

    const mergedAlgoliaOrderedRecords = algoliaAsRecords
      .filter((record) => Boolean(record.docId))
      .map((algoliaRecord) => {
        const docId = algoliaRecord.docId as string;
        const cacheRecord = cacheByDocId.get(docId);
        return cacheRecord || algoliaRecord;
      });

    const queryLower = currentQuery.toLowerCase().trim();
    const cacheOnlyRecords = filterCacheRecordsIncrementally(
      queryLower,
      Array.from(cacheByDocId.values()),
      algoliaDocIds,
      cacheRecords
    );

    return [...mergedAlgoliaOrderedRecords, ...cacheOnlyRecords];
  }, [currentQuery, algoliaResults, cacheRecords, filterCacheRecordsIncrementally]);

  return {
    searchResults,
    isSearching,
    searchError,
    search,
    clearSearch,
  };
};
